/**
 * A CHECAGEM ANTES DE ENVIAR, em três estados (07/10/2026).
 *
 * Era binária: as conferências do HUB passavam → "✓ Tudo certo para enviar ao portal". No PE 59/2026 a tela
 * mostrava, uma embaixo da outra, "Última automação falhou — BLL rejeitou a proposta: Houve um erro inesperado."
 * e "✓ Tudo certo para enviar ao portal.", depois de QUATRO envios recusados seguidos (06/10 19:02, 07/10 06:13,
 * 06:14 e 07:14). O histórico estava renderizado logo abaixo; a checagem só não olhava.
 *
 *  - verde:    as conferências passaram e não há envio recusado pendente;
 *  - atenção:  as conferências passaram, MAS a última tentativa de envio foi recusada (ou pediu ação manual),
 *              e a mensagem do portal vai junto: o operador precisa do motivo, não do fato;
 *  - vermelho: alguma conferência reprovou, como antes.
 *
 * ⚠️ O verde NÃO diz que o portal vai aceitar. Ele confere o que o HUB sabe; o que o PROCESSO exige no portal
 *    (ler o portal autenticado) é outra metade, no backend.
 *
 * Decisões:
 *  - Tentativa de envio = job que não é sincronização (`scrape_proposal`), nem desistência (`withdraw*`), nem
 *    teste (`dryRun`). O teste não chega a cadastrar no portal, então não prova nem desprova o envio.
 *  - A falha conta até haver um envio que deu certo, mesmo que a proposta tenha sido editada depois: editar não
 *    é prova de que consertou. O aviso mostra a data da tentativa, e quem editou sabe ler isso.
 *  - Proposta já enviada não ganha aviso: a falha velha não diz mais nada sobre ela.
 *  - "Na fila" ou "executando" não é atenção: a checagem já reprova por "automação em andamento".
 */

import { SENT_PROPOSAL_STATUSES } from './proposalStage';
import type { Carga } from '../design';

export type EstadoDaChecagem = 'verde' | 'atencao' | 'vermelho' | 'conferindo';

export interface TentativaRecusada {
  quando: string | null;
  mensagem: string;
  status: string;
}

const ehEnvio = (j: any) =>
  !!j && j.action !== 'scrape_proposal' && !String(j.action || '').startsWith('withdraw') && !j.dryRun;

const momento = (j: any) => new Date(j.createdAt || j.updatedAt || 0).getTime() || 0;

/** O envio mais recente da proposta, sem depender da ordem em que a lista chega. */
export function ultimaTentativaDeEnvio(jobs: any[] | null | undefined): any | null {
  const envios = (jobs || []).filter(ehEnvio);
  if (envios.length === 0) return null;
  return envios.reduce((a, b) => (momento(b) > momento(a) || (momento(b) === momento(a) && (b.id ?? 0) > (a.id ?? 0)) ? b : a));
}

/**
 * ⛔ O HISTÓRICO DE ENVIOS PRECISA SER CONHECIDO PARA O VERDE (07/10/2026). Com a carga dos jobs falhando, a lista
 *    chegava vazia e a checagem lia "nenhuma tentativa de envio" → verde, mesmo na proposta 379, com quatro envios
 *    recusados. É o alarme que não consegue disparar, pior que o contador que mente: do contador o operador
 *    desconfia; no verde ele acredita. Então:
 *    - `historico` com erro (ou parcial: parte dele não chegou) → atenção, dizendo que o histórico não carregou
 *      (`historicoIndisponivel`);
 *    - `historico` ainda carregando → 'conferindo', neutro, que também não é verde;
 *    - sem `historico` informado, vale como conhecido (quem chama sem carga, como os testes antigos).
 *    A tentativa recusada que já está na lista vem antes: ela diz mais que "não sei".
 */
export function estadoDaChecagem(input: {
  check: { ok: boolean };
  jobs?: any[] | null;
  proposal?: { status?: string; [k: string]: any } | null;
  historico?: Pick<Carga, 'estado' | 'erro'> | null;
}): { estado: EstadoDaChecagem; tentativa: TentativaRecusada | null; historicoIndisponivel?: string } {
  if (!input.check.ok) return { estado: 'vermelho', tentativa: null };
  if (SENT_PROPOSAL_STATUSES.has(input.proposal?.status as string)) return { estado: 'verde', tentativa: null };
  const ultima = ultimaTentativaDeEnvio(input.jobs);
  if (ultima && (ultima.status === 'failed' || ultima.status === 'needs_manual')) {
    return {
      estado: 'atencao',
      tentativa: {
        quando: ultima.updatedAt || ultima.createdAt || null,
        mensagem: String(ultima.errorMessage || '').trim() || 'O portal não informou o motivo.',
        status: ultima.status,
      },
    };
  }
  if (input.historico?.estado === 'erro' || input.historico?.estado === 'parcial') {
    return { estado: 'atencao', tentativa: null, historicoIndisponivel: input.historico.erro || 'erro desconhecido' };
  }
  if (input.historico?.estado === 'carregando') return { estado: 'conferindo', tentativa: null };
  return { estado: 'verde', tentativa: null };
}
